import React, { useEffect, useMemo, useState } from "react";
import { Col } from "react-bootstrap";
import {
  Button,
  ButtonGroup,
  Chip,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  makeStyles,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { Autocomplete } from "@material-ui/lab";
import { MDBDataTable } from "mdbreact";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MenuBook as MenuBookIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Search as SearchIcon,
} from "@material-ui/icons";
import swal from "sweetalert";
import { toast } from "react-hot-toast";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import exportFromJSON from "export-from-json";
import {
  deleteFilaHome,
  fetchLibrosFila,
  isEstaticoTrue,
  setOrdenFilaHome,
  toggleFilaHomeActivo,
} from "../../../../shared/fetchers/filasHome";
import { paisesAutocomplete } from "../../../../shared/data/paises";
import { columnasFilasHome } from "../../../../shared/data/columnas/classics/filasHome";
import CustomGreenButton from "../../../../components/shared/custom-green-button";
import ExcelWorkBook from "../../../../components/shared/ExcelWorkBook";

const isActivoTrue = (val) =>
  val === true || val === 1 || val === "1" || val === "true";

const ListaFilas = ({
  filasHome = [],
  loading,
  onCrear,
  onEditar,
  onGestionarLibros,
}) => {
  const classes = useStyles();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("todas");
  const [paisFiltro, setPaisFiltro] = useState(null);
  const [columns, setColumns] = useState(columnasFilasHome);

  const librosFilaQueries = useQueries({
    queries: filasHome.map((f) => ({
      queryKey: ["librosFila", f.filaHomeName],
      queryFn: () => fetchLibrosFila(f.filaHomeName),
      enabled: Boolean(f.filaHomeName),
      staleTime: 60_000,
    })),
  });

  const conteosLibros = useMemo(() => {
    const map = {};
    filasHome.forEach((f, i) => {
      map[f.filaHomeName] = librosFilaQueries[i]?.data?.length;
    });
    return map;
  }, [filasHome, librosFilaQueries]);

  const filasFiltradas = useMemo(() => {
    const term = search.trim().toLowerCase();
    return filasHome.filter((fila) => {
      if (term) {
        const haystack = `${fila.filaHomeName ?? ""} ${fila.description ?? ""} ${
          fila.filaHomeNameEng ?? ""
        } ${fila.descriptionEng ?? ""}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (estadoFiltro !== "todas") {
        const isActive = isActivoTrue(fila.activo);
        if (estadoFiltro === "activas" && !isActive) return false;
        if (estadoFiltro === "inactivas" && isActive) return false;
      }
      if (paisFiltro) {
        const paises = (fila.disponibilidadRegional ?? "")
          .split(",")
          .map((p) => p.trim());
        if (!paises.includes(paisFiltro)) return false;
      }
      return true;
    });
  }, [filasHome, search, estadoFiltro, paisFiltro]);

  const handleToggleActivo = async (fila) => {
    const next = !isActivoTrue(fila.activo);
    queryClient.setQueryData(["filasHome"], (prev) =>
      Array.isArray(prev)
        ? prev.map((f) =>
            f.filaHomeName === fila.filaHomeName
              ? { ...f, activo: next ? "1" : "0" }
              : f
          )
        : prev
    );

    try {
      const res = await toggleFilaHomeActivo({
        nombre: fila.filaHomeName,
        activo: next,
        fila,
      });
      if (!res?.status) throw new Error(res?.info ?? "Error");
      toast.success(
        `Fila "${fila.filaHomeName}" ${next ? "activada" : "desactivada"}`
      );
      queryClient.invalidateQueries({ queryKey: ["filasHome"] });
    } catch (error) {
      toast.error(error?.message || "Error cambiando el estado");
      queryClient.invalidateQueries({ queryKey: ["filasHome"] });
    }
  };

  const handleEliminar = async (fila) => {
    const cantidad = conteosLibros[fila.filaHomeName];
    const cantidadTexto =
      typeof cantidad === "number"
        ? `Esta fila tiene ${cantidad} libro${
            cantidad === 1 ? "" : "s"
          } asignado${cantidad === 1 ? "" : "s"}.`
        : "";

    const verify = await swal({
      title: "¿Eliminar fila?",
      text: `Vas a eliminar "${fila.filaHomeName}". ${cantidadTexto} Esta acción no se puede deshacer.`,
      icon: "warning",
      buttons: ["Cancelar", "Eliminar"],
      dangerMode: true,
    });
    if (!verify) return;

    try {
      const res = await deleteFilaHome(fila.filaHomeName);
      if (!res?.status) throw new Error(res?.info ?? "Error");
      toast.success("Fila eliminada");
      queryClient.invalidateQueries({ queryKey: ["filasHome"] });
    } catch (error) {
      toast.error(error?.message || "Error eliminando la fila");
    }
  };

  const handleMover = async (fila, direccion) => {
    if (isEstaticoTrue(fila.estatico)) {
      toast.error("Esta fila tiene posición fija y no se puede reordenar.");
      return;
    }

    const filasActivas = filasHome.filter((f) => isActivoTrue(f.activo));
    const idxActual = filasActivas.findIndex(
      (f) => f.filaHomeName === fila.filaHomeName
    );
    const nuevoIndice = idxActual + direccion;
    if (nuevoIndice < 0 || nuevoIndice >= filasActivas.length) return;

    const vecina = filasActivas[nuevoIndice];
    if (vecina && isEstaticoTrue(vecina.estatico)) {
      toast.error("No se puede intercambiar posición con una fila fija.");
      return;
    }

    const nuevasActivas = [...filasActivas];
    [nuevasActivas[idxActual], nuevasActivas[nuevoIndice]] = [
      nuevasActivas[nuevoIndice],
      nuevasActivas[idxActual],
    ];

    queryClient.setQueryData(["filasHome"], (prev) => {
      if (!Array.isArray(prev)) return prev;
      const inactivas = prev.filter((f) => !isActivoTrue(f.activo));
      return [...nuevasActivas, ...inactivas];
    });

    const filasOrdenadas = nuevasActivas.map((f) => ({
      filaHome: f.filaHomeName,
    }));

    try {
      const res = await setOrdenFilaHome({ filasOrdenadas });
      if (!res?.status) throw new Error(res?.info ?? "Error reordenando");
    } catch (error) {
      toast.error(error?.message || "Error reordenando la fila");
      queryClient.invalidateQueries({ queryKey: ["filasHome"] });
    }
  };

  const renderPaisesChips = (raw) => {
    if (!raw) return <span className="admin-muted-text-small">Global</span>;
    const arr = String(raw)
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    if (!arr.length)
      return <span className="admin-muted-text-small">Global</span>;
    return (
      <div className={classes.chipsRow}>
        {arr.slice(0, 3).map((pais) => (
          <Chip
            key={pais}
            size="small"
            label={pais}
            className={classes.countryChip}
          />
        ))}
        {arr.length > 3 && (
          <Tooltip title={arr.slice(3).join(", ")}>
            <Chip
              size="small"
              label={`+${arr.length - 3}`}
              className={classes.countryChip}
            />
          </Tooltip>
        )}
      </div>
    );
  };

  const rows = useMemo(() => {
    const filasActivas = filasHome.filter((f) => isActivoTrue(f.activo));

    return filasFiltradas.map((fila) => {
      const isActive = isActivoTrue(fila.activo);
      const idxActivo = isActive
        ? filasActivas.findIndex((f) => f.filaHomeName === fila.filaHomeName)
        : -1;
      const filaEstatica = isEstaticoTrue(fila.estatico);
      const prevEsEstatica =
        idxActivo > 0 && isEstaticoTrue(filasActivas[idxActivo - 1]?.estatico);
      const nextEsEstatica =
        idxActivo >= 0 &&
        idxActivo < filasActivas.length - 1 &&
        isEstaticoTrue(filasActivas[idxActivo + 1]?.estatico);
      const isFirst = idxActivo === 0;
      const isLast = idxActivo === filasActivas.length - 1;
      const blockUp = !isActive || isFirst || filaEstatica || prevEsEstatica;
      const blockDown = !isActive || isLast || filaEstatica || nextEsEstatica;
      const totalLibros = conteosLibros[fila.filaHomeName];

      return {
        ...fila,
        filaHomeNameEng: fila.filaHomeNameEng?.trim()
          ? fila.filaHomeNameEng
          : "—",
        descriptionEng: fila.descriptionEng?.trim()
          ? fila.descriptionEng
          : "—",
        estatico: filaEstatica ? (
          <Chip size="small" label="Fija" className={classes.estaticoChip} />
        ) : (
          <span className="admin-muted-text-small">—</span>
        ),
        orden: (
          <div className={classes.ordenCell}>
            <span className={classes.ordenNumber}>
              {isActive ? idxActivo + 1 : <span style={{ color: "#bbb" }}>—</span>}
            </span>
            <div className={classes.ordenArrows}>
              <Tooltip
                title={
                  filaEstatica
                    ? "Fila con posición fija"
                    : prevEsEstatica
                      ? "No se puede pasar por encima de una fila fija"
                      : isActive
                        ? "Subir"
                        : "Solo aplica en activas"
                }
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={blockUp}
                    onClick={() => handleMover(fila, -1)}
                  >
                    <ArrowUpwardIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  filaEstatica
                    ? "Fila con posición fija"
                    : nextEsEstatica
                      ? "No se puede pasar por debajo de una fila fija"
                      : isActive
                        ? "Bajar"
                        : "Solo aplica en activas"
                }
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={blockDown}
                    onClick={() => handleMover(fila, 1)}
                  >
                    <ArrowDownwardIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </div>
        ),
        disponibilidadRegional: renderPaisesChips(fila.disponibilidadRegional),
        totalLibros:
          typeof totalLibros === "number" ? (
            <Chip
              size="small"
              label={totalLibros}
              className={classes.countChip}
            />
          ) : (
            <Skeleton variant="text" width={30} />
          ),
        estado: (
          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <Switch
              checked={isActive}
              onChange={() => handleToggleActivo(fila)}
              color="primary"
              size="small"
            />
            <span
              className={`badge ${
                isActive ? "badge-success" : "badge-secondary"
              }`}
            >
              {isActive ? "Activa" : "Inactiva"}
            </span>
          </div>
        ),
        acciones: (
          <div className={classes.actionsCell}>
            <button
              className={`${classes.actionBtn} ${classes.editBtn}`}
              onClick={() => onEditar?.(fila)}
              title="Editar"
            >
              <EditIcon style={{ fontSize: 14, marginRight: 3 }} />
              Editar
            </button>
            <button
              className={`${classes.actionBtn} ${classes.booksBtn}`}
              onClick={() => onGestionarLibros?.(fila)}
              title="Gestionar libros"
            >
              <MenuBookIcon style={{ fontSize: 14, marginRight: 3 }} />
              Libros
            </button>
            <button
              className={`${classes.actionBtn} ${classes.deleteBtn}`}
              onClick={() => handleEliminar(fila)}
              title="Eliminar"
            >
              <DeleteIcon style={{ fontSize: 14, marginRight: 3 }} />
              Eliminar
            </button>
          </div>
        ),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filasFiltradas, filasHome, conteosLibros]);

  useEffect(() => {
    setColumns(columnasFilasHome);
  }, []);

  const visibleColumns = columns.filter((c) => c.isVisible !== false);

  const exportRows = useMemo(
    () =>
      filasFiltradas.map((fila, idx) => ({
        orden: idx + 1,
        filaHomeName: fila.filaHomeName,
        description: fila.description,
        filaHomeNameEng: fila.filaHomeNameEng || "—",
        descriptionEng: fila.descriptionEng || "—",
        estatico: isEstaticoTrue(fila.estatico) ? "Sí" : "No",
        disponibilidadRegional: fila.disponibilidadRegional || "Global",
        totalLibros: conteosLibros[fila.filaHomeName] ?? "—",
        estado: isActivoTrue(fila.activo) ? "Activa" : "Inactiva",
      })),
    [filasFiltradas, conteosLibros]
  );

  return (
    <Col xs={12} className="content-white-box px-4 py-4">
      <div className="d-flex align-items-center justify-content-between flex-wrap">
        <div>
          <div className="d-flex align-items-center">
            <MenuBookIcon style={{ fontSize: 22 }} />
            <div className="admin-main-title-dark-gray ml-2 mt-1">
              Filas Home
            </div>
          </div>
          <div className="admin-muted-text-small mt-1">
            Administra las filas de libros que se muestran en el home. Activa,
            desactiva, ordena y gestiona los libros que pertenecen a cada una.
          </div>
        </div>

        <CustomGreenButton
          variant="contained"
          size="medium"
          startIcon={<AddIcon />}
          onClick={onCrear}
        >
          Crear fila
        </CustomGreenButton>
      </div>

      <div className={classes.filtersBar}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Buscar por nombre o descripción"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <SearchIcon style={{ fontSize: 18, color: "#888", marginRight: 6 }} />
            ),
          }}
          style={{ flex: 1, minWidth: 240 }}
        />
        <Select
          variant="outlined"
          value={estadoFiltro}
          onChange={(e) => setEstadoFiltro(e.target.value)}
          style={{ minWidth: 160 }}
          margin="dense"
        >
          <MenuItem value="todas">Todas</MenuItem>
          <MenuItem value="activas">Solo activas</MenuItem>
          <MenuItem value="inactivas">Solo inactivas</MenuItem>
        </Select>
        <Autocomplete
          options={paisesAutocomplete}
          value={paisFiltro}
          onChange={(_, value) => setPaisFiltro(value)}
          getOptionLabel={(o) => o ?? ""}
          noOptionsText="Sin resultados"
          style={{ minWidth: 200 }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder="Filtrar por país"
            />
          )}
        />
      </div>

      <div className={classes.summaryRow}>
        <Chip
          size="small"
          label={`Total: ${filasHome.length}`}
          variant="outlined"
        />
        <Chip
          size="small"
          label={`Activas: ${
            filasHome.filter((f) => isActivoTrue(f.activo)).length
          }`}
          style={{ background: "#e6f7f1", color: "#2f8270" }}
        />
        <Chip
          size="small"
          label={`Mostrando: ${filasFiltradas.length}`}
          variant="outlined"
        />
      </div>

      {filasHome.length > 0 && (
        <ButtonGroup size="small" variant="contained" className="my-3">
          <CustomGreenButton
            onClick={() => {
              navigator.clipboard
                .writeText(JSON.stringify(exportRows))
                .then(() =>
                  swal({
                    title: "¡Tabla copiada!",
                    icon: "success",
                  })
                );
            }}
          >
            Copy
          </CustomGreenButton>
          <CustomGreenButton
            onClick={() =>
              exportFromJSON({
                data: exportRows,
                fileName: "filas-home",
                exportType: exportFromJSON.types.csv,
              })
            }
          >
            CSV
          </CustomGreenButton>
          <CustomGreenButton>
            <ExcelWorkBook
              columns={visibleColumns
                .filter((c) => c.field !== "acciones")
                .map((c) => ({ label: c.label, field: c.field, isVisible: true }))}
              rows={exportRows}
              filename={"filas-home"}
            />
          </CustomGreenButton>
        </ButtonGroup>
      )}

      {loading ? (
        <>
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
        </>
      ) : filasHome.length === 0 ? (
        <div className={classes.empty}>
          <MenuBookIcon style={{ fontSize: 36, color: "#bbb" }} />
          <div className="mt-2 admin-muted-text-small">
            Aún no hay filas creadas.
          </div>
          <Button
            variant="contained"
            color="primary"
            className="mt-3"
            startIcon={<AddIcon />}
            onClick={onCrear}
          >
            Crear primera fila
          </Button>
        </div>
      ) : filasFiltradas.length === 0 ? (
        <div className={classes.empty}>
          <div className="admin-muted-text-small">
            No hay filas que coincidan con los filtros aplicados.
          </div>
          <Button
            variant="outlined"
            size="small"
            className="mt-3"
            onClick={() => {
              setSearch("");
              setEstadoFiltro("todas");
              setPaisFiltro(null);
            }}
          >
            Limpiar filtros
          </Button>
        </div>
      ) : (
        <MDBDataTable
          small
          responsive
          striped
          bordered
          searchTop={false}
          searchBottom={false}
          pagingTop
          data={{ columns: visibleColumns, rows }}
          noBottomColumns
          entriesOptions={[5, 10, 20, 50]}
          entriesLabel="Filas por página"
          paginationLabel={["Anterior", "Siguiente"]}
          infoLabel={["Mostrando", "a", "de", "filas"]}
        />
      )}
    </Col>
  );
};

export default ListaFilas;

const useStyles = makeStyles({
  filtersBar: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: 16,
    marginBottom: 8,
  },
  summaryRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 4,
  },
  ordenCell: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  ordenNumber: {
    fontWeight: 600,
    width: 22,
    textAlign: "center",
  },
  ordenArrows: {
    display: "flex",
    flexDirection: "column",
  },
  chipsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
  },
  countryChip: {
    height: 22,
    fontSize: "0.72em",
    background: "#eef5fb",
    color: "#1976d2",
  },
  countChip: {
    background: "#5fbeaa",
    color: "#fff",
    fontWeight: 600,
  },
  estaticoChip: {
    background: "#ffe8cc",
    color: "#b35900",
    fontWeight: 600,
  },
  actionsCell: {
    display: "flex",
    gap: 4,
    flexWrap: "wrap",
  },
  actionBtn: {
    border: "none",
    padding: "3px 8px",
    fontWeight: 500,
    color: "#fff",
    borderRadius: 3,
    fontSize: "0.78em",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
  },
  editBtn: {
    backgroundColor: "#5fbeaa",
  },
  booksBtn: {
    backgroundColor: "#007bff",
  },
  deleteBtn: {
    backgroundColor: "#dc3545",
  },
  empty: {
    textAlign: "center",
    padding: "40px 0",
  },
});
